import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Paper } from '@mui/material';
import useApi from '../../../hooks/useApi';
import { buildAccountModel, buildAccountSummary } from './appsheetAccountUtils';
import { getFirstCaseInsensitiveValue, getOrderItemAccountId } from './appsheetFieldUtils';
import { TRADE_VMT_RECORD_DEFAULTS, TRADE_VMT_RECORD_OVERRIDES } from './appsheetVmtMapping';
import { sumOrderItemsMrr, getOrderItemProgramName, isAccountCancelled } from './appsheetMrrUtils';
import AppSheetAccountCardsRow from './AppSheetAccountCardsRow';
import AppSheetAccountDetailPanel from './AppSheetAccountDetailPanel';
import AppSheetOrderItemsTable from './AppSheetOrderItemsTable';
import AppSheetEditAndSendVmt from './AppSheetEditAndSendVmt';
import DetailPanelScrollableTableSection from '../components/DetailPanelScrollableTableSection.jsx';
import {
  DETAIL_PANEL_MIN_TABLE_VIEWPORT_HEIGHT,
  DETAIL_PANEL_MIN_TABLE_VIEWPORT_PX,
} from '../utils/detailPanelTableLayout';

const normalizeKey = (value) => String(value ?? '').trim().toLowerCase();
const APPSHEET_VARIANTS = {
  appsheet: {
    tableKey: 'orderItems',
    panelLabel: 'AppSheet',
    itemLabel: 'Order item',
    itemLabelPlural: 'order items',
    emptyMessage: 'No AppSheet order items found for this BAC.',
    showMrr: true,
    vmtRecordDefaults: null,
    vmtRecordOverrides: null,
  },
  trade: {
    tableKey: 'tradeLineItems',
    panelLabel: 'Trade',
    itemLabel: 'Trade line item',
    itemLabelPlural: 'trade line items',
    emptyMessage: 'No Trade line items found for this BAC.',
    showMrr: false,
    vmtRecordDefaults: TRADE_VMT_RECORD_DEFAULTS,
    vmtRecordOverrides: TRADE_VMT_RECORD_OVERRIDES,
  },
};

// DetailPanelAppSheetTab is the AppSheet/Trade dealer-context drawer surface.
// One component handles both variants so the account/order-item workflow stays
// consistent while only the data source and mapping rules change.
const DetailPanelAppSheetTab = ({
  bac,
  userEmail,
  initialSelectedAccountId = null,
  variant = 'appsheet',
  onNotify = null,
  programName = null,
}) => {
  const { useAppsheetDealerContext } = useApi();
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const config = APPSHEET_VARIANTS[variant] || APPSHEET_VARIANTS.appsheet;
  const { data, isLoading, error } = useAppsheetDealerContext(bac, {
    includeTrade: config.tableKey === 'tradeLineItems',
  });

  useEffect(() => {
    if (initialSelectedAccountId) {
      setSelectedAccountId(String(initialSelectedAccountId));
      return;
    }
    setSelectedAccountId(null);
  }, [bac, initialSelectedAccountId]);

  useEffect(() => {
    setEditingRow(null);
  }, [bac, programName, variant]);

  const accounts = useMemo(() => (data?.accounts || []).map(buildAccountModel), [data?.accounts]);
  const accountMrrById = useMemo(() => {
    const map = new Map();
    // Per-account MRR is derived from the visible order-item dataset so the
    // summary cards align with the table the operator is actually reviewing.
    (data?.[config.tableKey] || []).forEach((row) => {
      if (!row) return;
      const accountId = getOrderItemAccountId(row);
      if (!accountId) return;
      const key = normalizeKey(accountId);
      const price = sumOrderItemsMrr([row], { targetProgram: getOrderItemProgramName(row) });
      if (!price) return;
      map.set(key, (map.get(key) || 0) + price);
    });
    return map;
  }, [config.tableKey, data]);

  const accountsWithMrr = useMemo(
    () =>
      accounts.map((account) => ({
        ...account,
        mrr: accountMrrById.get(normalizeKey(account.id)) || 0,
      })),
    [accounts, accountMrrById]
  );

  const accountsById = useMemo(() => {
    const map = new Map();
    accountsWithMrr.forEach((account) => {
      if (!account.id) return;
      map.set(normalizeKey(account.id), account);
    });
    return map;
  }, [accountsWithMrr]);

  const accountSummary = useMemo(() => {
    const summary = buildAccountSummary(accountsWithMrr, bac || '');
    const totalMrr = accountsWithMrr.reduce((sum, account) => {
      if (isAccountCancelled(account.status)) return sum;
      return sum + (account.mrr || 0);
    }, 0);
    return { ...summary, mrr: totalMrr };
  }, [accountsWithMrr, bac]);

  const selectedAccount = useMemo(() => {
    if (!selectedAccountId) return null;
    return accountsById.get(normalizeKey(selectedAccountId)) || null;
  }, [accountsById, selectedAccountId]);

  const orderItems = useMemo(
    () => (data?.[config.tableKey] || []).map((row) => row || {}),
    [config.tableKey, data]
  );
  const orderItemsWithQuantity = useMemo(
    () =>
      orderItems.filter((row) => {
        const rawQuantity = getFirstCaseInsensitiveValue(row, [
          'itemQuantity',
          'ItemQuantity',
          'item_quantity',
          'Item_Quantity',
          'quantity',
          'Quantity',
          'itemQty',
          'ItemQty',
        ]);
        if (rawQuantity === undefined || rawQuantity === null) return true;
        const normalized = String(rawQuantity).trim();
        if (!normalized) return true;
        const numeric = Number(normalized);
        if (!Number.isFinite(numeric)) return true;
        return numeric >= 0;
      }),
    [orderItems]
  );
  const filteredOrderItems = useMemo(() => {
    if (!selectedAccountId) return orderItemsWithQuantity;
    const target = normalizeKey(selectedAccountId);
    return orderItemsWithQuantity.filter((row) => {
      const accountId = getOrderItemAccountId(row);
      return normalizeKey(accountId) === target;
    });
  }, [orderItemsWithQuantity, selectedAccountId]);

  const schemaWarnings = useMemo(() => {
    const tableConfigured =
      data?.schema?.[config.tableKey]?.tableConfigured ?? Boolean(data?.tables?.[config.tableKey]);
    if (!tableConfigured) {
      return {
        missingAccounts: [],
        missingOrderItems: [],
        tableConfigured: false,
      };
    }
    const missingAccounts = data?.schema?.accounts?.missingFields || [];
    const missingOrderItems = data?.schema?.[config.tableKey]?.missingFields || [];
    if (!missingAccounts.length && !missingOrderItems.length) return null;
    return { missingAccounts, missingOrderItems, tableConfigured: true };
  }, [config.tableKey, data?.schema, data?.tables]);

  return (
    <Box
      data-testid={`detail-panel-${variant}-tab`}
      sx={{
        height: '100%',
        flex: '1 1 auto',
        minHeight: 0,
        pt: 2,
        px: 2,
        pb: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
      }}
    >

      {schemaWarnings ? (
        schemaWarnings.tableConfigured === false ? (
          <Alert severity="info">{`${config.panelLabel} table is not configured yet.`}</Alert>
        ) : (
          <Alert severity="warning">
            {`Missing required ${config.panelLabel} fields.`}
            {schemaWarnings.missingAccounts.length ? ` Accounts: ${schemaWarnings.missingAccounts.join(', ')}.` : ''}
            {schemaWarnings.missingOrderItems.length ? ` ${config.itemLabel}s: ${schemaWarnings.missingOrderItems.join(', ')}.` : ''}
          </Alert>
        )
      ) : null}

      {error ? (
        <Alert severity="error">{error?.message || `Failed to load ${config.panelLabel} data.`}</Alert>
      ) : null}

      {isLoading ? (
        <Alert severity="info">{`Loading ${config.panelLabel} data...`}</Alert>
      ) : (
        <>
          {editingRow ? (
            <AppSheetEditAndSendVmt
              row={editingRow}
              bac={bac}
              userEmail={userEmail}
              variant={variant}
              programName={programName}
              defaults={config.vmtRecordDefaults}
              overrides={config.vmtRecordOverrides}
              onBack={() => setEditingRow(null)}
              onNotify={onNotify}
            />
          ) : (
            <>
              {accounts.length === 0 ? (
                <Alert severity="info">{`No ${config.panelLabel} accounts found for this BAC.`}</Alert>
              ) : null}
              <Box sx={{ flexShrink: 0 }}>
                <AppSheetAccountCardsRow
                  accounts={accountsWithMrr}
                  selectedAccountId={selectedAccountId}
                  onSelectAccount={setSelectedAccountId}
                />
              </Box>

              <Paper elevation={2} sx={{ overflow: 'hidden', flexShrink: 0 }}>
                <AppSheetAccountDetailPanel
                  account={selectedAccount}
                  summary={accountSummary}
                  isAllAccounts={!selectedAccountId}
                  showMrr={config.showMrr}
                />
              </Paper>
              <DetailPanelScrollableTableSection
                data-testid={`detail-panel-${variant}-table-section`}
                minHeight={DETAIL_PANEL_MIN_TABLE_VIEWPORT_HEIGHT}
                bodySx={{ p: 0 }}
              >
                {/* The drawer keeps its own vertical scroll, but the embedded MRT grid
                    still needs real internal row/column overflow for dense tables. */}
                <AppSheetOrderItemsTable
                  rows={filteredOrderItems}
                  fields={data?.tables?.[config.tableKey]?.fields || []}
                  accountsById={accountsById}
                  userEmail={userEmail}
                  onEditAndSendVmt={setEditingRow}
                  itemLabel={config.itemLabel}
                  itemLabelPlural={config.itemLabelPlural}
                  emptyMessage={config.emptyMessage}
                  vmtRecordDefaults={config.vmtRecordDefaults}
                  vmtRecordOverrides={config.vmtRecordOverrides}
                  enableInternalScroll
                  minTableHeight={DETAIL_PANEL_MIN_TABLE_VIEWPORT_PX}
                  hideFullScreenToggle
                  tableContainerTestId={`detail-panel-${variant}-table-container`}
                  summary={{
                    accounts: accounts.length,
                    orderItems: orderItemsWithQuantity.length,
                    filteredOrderItems: selectedAccountId ? filteredOrderItems.length : null,
                  }}
                />
              </DetailPanelScrollableTableSection>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default DetailPanelAppSheetTab;
